<template>
  <v-sheet
    v-if="!isHeaderOrFooterField(type)"
    v-show="!shouldHideIfEmpty({
      type, sections, isTemplate, isFlagship,
    })"
    class="field"
    :class="{
      last: Boolean(last),
    }"
  >
    <FieldLabel>
      {{ label }}
    </FieldLabel>
    <TrSection
      v-for="(section, sectionIndex) in sections.length > 0 ? sections : [{ type: 'default', blocks: [], formatBlacklist: [] }]"
      :id="section.id"
      :ref="`section-${section.id}`"
      :key="section.id"
      :field-id="id"
      :field-index="index"
      :field-type="type"
      :index="sectionIndex"
      :blocks="section.blocks"
      :section-nudges="sectionNudges"
      :block-nudges="blockNudges"
      :first="sectionIndex === 0"
      :last="sectionIndex === sections.length - 1"
      :only="sections.length === 1"
      :type="section.type"
      :format-blacklist="Array.from(new Set([...formatBlacklist, ...section.formatBlacklist]))"
      :lock-type="section.lockType"
      :job="job"
      :update-and-save="updateAndSave"
      :editors-init-state="editorsInitState"
      :description="description"
      :editor-initialized-action="editorInitializedAction"
      :is-template="isTemplate"
      :is-flagship="isFlagship"
      :is-editable="isEditable"
      :add-section="addSection"
      :delete-section="deleteSection"
      :swap-sections="swapSections"
      :next-section="sections[sectionIndex + 1]"
      :prev-section="sections[sectionIndex - 1]"
      :focus-section="focusSection"
      :focus="focus"
    />
  </v-sheet>
</template>

<script>
/* eslint-disable vue/require-prop-types */
import { v4 as uuidv4 } from 'uuid';
import { mapState } from 'pinia';
import { useEditor } from '@/stores/editor/editor';
import { shouldHideIfEmpty } from '@/helpers/constants/hideEmptyFields';
import { isHeaderOrFooterField } from '@/helpers/constants/lockableFields';
import { nextTick } from 'vue';
import FieldLabel from './FieldLabel.vue';
import TrSection from '../TrSection.vue';

export default {
  name: 'TrField',
  components: { FieldLabel, TrSection },
  props: [
    'id',
    'type',
    'formatBlacklist',
    'label',
    'index',
    'last',
    'sections',
    'job',
    'updateAndSave',
    'editorsInitState',
    'description',
    'setDescriptionAction',
    'editorDataSource',
    'setHandleCleanupBeforeRescoreAction',
    'editorInitializedAction',
    'projectIntegrationType',
    'isTemplate',
    'isFlagship',
    'historyStack',
    'isEditable',
    'only',
    'focus',
    'sectionNudges',
    'blockNudges',
  ],
  computed: {
    ...mapState(useEditor, ['revisionView']),
  },
  methods: {
    isHeaderOrFooterField,
    shouldHideIfEmpty,

    addSection(change, { recordHistory = true } = {}) {
      const sectionIndex = this.sections
        .findIndex((section) => change.prevSectionId === section.id) + 1;
      // eslint-disable-next-line vue/no-mutating-props
      this.sections.splice(sectionIndex, 0, change.content);
      if (recordHistory) {
        this.historyStack.record(change);
      }
    },

    deleteSection(change, { recordHistory = true } = {}) {
      const sectionIndex = this.sections
        .findIndex((section) => change.prevSectionId === section.id) + 1;
      // eslint-disable-next-line vue/no-mutating-props
      this.sections.splice(sectionIndex, 1);
      if (recordHistory) {
        this.historyStack.record(change);
      }
    },

    swapSections(change, { recordHistory = true } = {}) {
      const sectionIndex1 = this.sections.findIndex((section) => change.sectionId1 === section.id);
      const sectionIndex2 = this.sections.findIndex((section) => change.sectionId2 === section.id);
      [this.sections[sectionIndex1], this.sections[sectionIndex2]] = [
        this.sections[sectionIndex2], this.sections[sectionIndex1],
      ];
      if (recordHistory) {
        this.historyStack.record(change);
      }
      this.$forceUpdate();
    },
    addFirstSection(section) {
      const change = {
        changeType: 'create-section',
        fieldId: this.id,
        prevSectionId: null,
        history: null,
        content: {
          id: uuidv4(),
          type: section.type,
          lockType: section.lockType,
          formatBlacklist: section.formatBlacklist,
          blocks: section.blocks.map((block) => ({ ...block, id: uuidv4() })),
          highlights: [],
        },
      };
      this.addSection(change);

      const { id, blocks } = change.content;
      nextTick(() => {
        this.focusSection(id, blocks[0].id);
        this.updateAndSave();
      });
    },
    focusSection(sectionId, blockId) {
      setTimeout(() => {
        const section = this.$refs[`section-${sectionId}`][0];
        const block = section.$refs[`block-${blockId}`][0];
        block.$el.querySelector('.ql-editor').click();
      }, 0);
    },

    initialized() {
      return this.sections.every((section) => section.initialized());
    },
  },
};
</script>

<style lang="less" scoped>
.field {
  margin-top: 8px;
  position: relative;
  padding: 24px 0 24px 0;
  min-height: 40px;
  &.email {
    background-color: transparent;
    box-shadow: none;
    padding: 0;
  }

  .field-label {
    position: absolute;
    top: 4px;
    right: 12px;
  }
  .field-label-inner {
    position: absolute;
    top: -20px;
    right: 20px;
  }

  .dummy-section {
    display: flex;
  }
  .dummy-add-section-dropdown {
    // make click zone of the dropdown cover the whole dummy-section
    padding: 60px 440px 0 440px;
    margin: -60px -440px 0 -440px;
    cursor: pointer;

    &.side-by-side {
      padding: 60px 286px 0 286px;
      margin: -60px -286px 0 -286px;
    }
  }
  .dummy-editor {
    color: fade(@grey-900, 40%);
    margin: 10px auto 0 auto;
  }

  .v-list-item__title {
    font-size: 13px !important;
    font-weight: 400 !important;
    color: #77818F !important;
  }

  .email .dummy-section {
    border-radius: 10px;
    padding: 16px 24px;
    background-color: @white;
    min-height: 40px;
  }
}
</style>
